import { ReactNode } from 'react';
import { useLocation, useInRouterContext } from 'react-router-dom';
import { HelpTip } from '../organisms/HelpTip';
import { helpForRoute } from '../help/registry';

/**
 * PageHeader — the canonical list/report page header: an <h1> title (with an
 * optional subtitle/count line) on the left and a right-aligned action-button
 * cluster (Columns / Refresh / Export / + New …).
 *
 * Why this exists (mobile-responsive tightening): list and report pages each
 * rolled their own `<header style={{ display:'flex', justifyContent:'space-
 * between' }}>` with a non-wrapping action `<div>`. On a ~390–430px viewport
 * (iPhone 13 Pro Max) the action cluster overflowed horizontally and the
 * primary button was clipped off the right edge. Centralizing the layout here
 * makes the fix systemic:
 *   - the whole header `flex-wrap`s, so the action cluster drops to a second
 *     line when the title + buttons can't share one row;
 *   - the action cluster itself `flex-wrap`s and stays right-aligned, so no
 *     button is ever clipped;
 *   - the title block can shrink (`minWidth: 0`) so a long title truncates
 *     rather than shoving the buttons off-canvas.
 *
 * Desktop is unchanged: at ≥ the width where everything fits on one row, wrap
 * never triggers and the layout is identical to the previous bespoke headers.
 *
 * Token discipline: title → --fs-3xl / --color-ink; subtitle → --color-ink-3.
 * Purely presentational — matches the visual the list pages already shipped.
 */

export interface PageHeaderProps {
  title: ReactNode;
  /** Optional supporting line under the title (e.g. "1,204 parcels"). */
  subtitle?: ReactNode;
  /** Right-aligned action-button cluster. */
  actions?: ReactNode;
  /**
   * Contextual-help article for this screen.
   *
   * Omit it and the header derives one from the route, so a page picks up help
   * without an edit — which is the point: a per-page opt-in is a per-page thing
   * to forget, and the pages most likely to be forgotten are the obscure ones a
   * new user most needs explained.
   *
   * Pass `null` to suppress the icon on a screen where it would be noise.
   */
  helpTopic?: string | null;
}

/**
 * The route-derived help icon, split into its own component for one reason:
 * `useLocation()` throws outside a Router, and PageHeader is a presentational
 * shell component that must keep rendering standalone. Calling the hook in here
 * — behind the `useInRouterContext()` guard at the call site — means the hook
 * only ever runs where a Router exists, without PageHeader itself gaining a
 * router dependency it did not have before.
 */
function RouteHelpTip() {
  const { pathname } = useLocation();
  const topic = helpForRoute(pathname);
  return topic ? <HelpTip topic={topic} size={16} /> : null;
}

export function PageHeader({ title, subtitle, actions, helpTopic }: PageHeaderProps) {
  // Reads context; returns false rather than throwing when there is no Router.
  const inRouter = useInRouterContext();
  return (
    <header style={{
      marginBottom: 'var(--sp-5)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      flexWrap: 'wrap',
    }}>
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>
          {title}
          {/* `undefined` means "derive one from the route"; `null` means
              "deliberately none"; a string names the article explicitly. */}
          {helpTopic === undefined
            ? (inRouter ? <RouteHelpTip /> : null)
            : (helpTopic ? <HelpTip topic={helpTopic} size={16} /> : null)}
        </h1>
        {subtitle != null && subtitle !== false && (
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>{subtitle}</p>
        )}
      </div>
      {actions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>{actions}</div>
      )}
    </header>
  );
}
